import { ActionHandler, DispatchOptions, GetterTree, Store } from "vuex";
import { getCurrentInstance } from "vue";

/**
 * A Vuex Store with the given state. The default types of the Store's getters and dispatch
 * are overridden in favor of more strictly typed versions, based on the actual definitions.
 */
export type TypedLegacyVuexStore<
	State,
	Getters extends GetterTree<any, any>,
	Actions extends Record<string, any>
> =
	Omit<Store<State>, 'getters' | 'dispatch'> & {
		getters: ResolvedGetters<Getters>,
		dispatch: ResolvedDispatch<Actions>
	};

/**
 * Helper composable for accessing $store on the current component instance
 * @returns The current Vuex store instance if available, null otherwise
 */
export function useLegacyVuexStore<
	TStoreState,
	TGetters extends GetterTree<any, any> = GetterTree<any, any>,
	TActions = Record<string, any>,
>(): TypedLegacyVuexStore<TStoreState, TGetters, TActions> {
	const instance = getCurrentInstance()?.proxy;

	if (!instance) {
		// eslint-disable-next-line no-console
		console.warn("useLegacyVuexStore: called outside of a component");
		return null;
	}

	const store = instance.$store;
	if (!store) {
		// eslint-disable-next-line no-console
		console.warn("useLegacyVuexStore: no store attached");
		return null;
	}

	return store as unknown as TypedLegacyVuexStore<TStoreState, TGetters, TActions>;
}

/** Removes the first items from a tuple type */
type Tail<T extends [...unknown[]]> = T extends [infer _, ...infer L] ? L : [];

/**
 * Given an action tree definition type and one of its keys, returns the type of the key if
 * its corresponding value is a valid ActionHandler. Returns `never` otherwise.
 *
 * Used as the filter condition in {@link ActionMapping}
 */
type ActionMappingName<ActionTreeDef extends Record<string, any>, K extends keyof ActionTreeDef> =
	ActionTreeDef[K] extends ActionHandler<any, any> ? K : never

/**
 * Creates a new type from T, where the keys are action names, and the values are tuples
 * of the arguments for dispatching that action and the action's return type.
 *
 * Entries of T whose values are not valid Vuex ActionHandlers are removed from the resulting type.
 */
type ActionMapping<ActionTreeDef extends Record<string, any>> = {
	readonly [ActionName in keyof ActionTreeDef as ActionMappingName<ActionTreeDef, ActionName>]: [args: Tail<Parameters<ActionTreeDef[ActionName]>>, returnType: ReturnType<ActionTreeDef[ActionName]>];
}

type ActionFunctionArgs<ActionTreeDef extends Record<string, any>, ActionName extends keyof ActionTreeDef> =
	ActionName extends keyof ActionMapping<ActionTreeDef>
		? [] extends ActionMapping<ActionTreeDef>[ActionName][0]
			// case where action definition function doesn't have a payload param
			? [payload?: undefined]
			: ActionMapping<ActionTreeDef>[ActionName][0]
		: [payload?: any]
/**
 * Given an action tree definition type and an action name, returns a tuple type for the arguments to
 * a dispatch call for that action.
 */
type ActionDispatchArgs<ActionTreeDef extends Record<string, any>, ActionName extends keyof ActionTreeDef> = [
	...ActionFunctionArgs<ActionTreeDef, ActionName>,
	options?: DispatchOptions
];

/**
 * Given an action tree definition type and an action name, returns the result type of
 * a dispatch call for that action.
 */
type ActionDispatchReturn<T extends Record<string, any>, K extends keyof T> =
	Promise<Awaited<K extends keyof ActionMapping<T>
		? ActionMapping<T>[K][1]
		: any
	>>;

/**
 * Given an action tree definition, returns a typed store dispatch function type.
 *
 * e.g. if the action tree looks like this:
 * @example
 * ```typescript
 * const actions = {
 *     someAction({ state }, payload: SomeType): string {
 *         // ...
 *     },
 * } satisfies ActionTree<State, State>;
 *
 * // elsewhere
 * declare const dispatch: ResolvedDispatch<typeof actions>;
 * const x = await dispatch('someAction', payload);
 * //    |                         |            |
 * //    |                         |            enforced by type system as `SomeType` because of 'someAction' arg
 * //    |                         |
 * //    |                         this gets autocompleted
 * //    |
 * //    this will have type `string`
 * ```
 */
type ResolvedDispatch<ActionTreeDef extends Record<string, any>> = {} extends ActionMapping<ActionTreeDef>
	? Store<any>['dispatch']
	: <A extends keyof ActionTreeDef>(action: A, ...args: ActionDispatchArgs<ActionTreeDef, A>) => ActionDispatchReturn<ActionTreeDef, A>;

type ResolvedGetters<T extends GetterTree<any, any>> = {
	readonly [K in keyof T]: ReturnType<T[K]>;
};
